import {
  ActionIcon, Badge, Button, CloseButton, Group, Paper, ScrollArea, SegmentedControl, SimpleGrid, Skeleton, Stack, Table, Text, TextInput,
  ThemeIcon, Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconAlarm, IconArrowBackUp, IconChevronRight, IconCircleCheck, IconInbox, IconMapPin, IconRefresh, IconSearch, IconUser,
  IconUserPlus,
} from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { Empty, fmtDate, PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";
import { AssignModal, CaseStatusBadge, CasesStyle, DueText, OPEN_STATUSES } from "./common";

interface CaseRow {
  id: number; number: string; kind: string; kind_label: string; status: string; status_label: string; bpg: string | null;
  applicant_name: string | null; applicant_email: string | null; holding_name: string | null; assigned_to: string | null;
  assigned_name: string | null; due_date: string | null; submitted_at: string | null; updated_at: string; decided_at: string | null;
  opstina: string | null; overdue: boolean | null; parcels: number;
}
interface Stats { ceka_dodjelu: number; otvoreni: number; moji: number; preko_roka: number; na_dopuni: number; odobreno_30d: number }

type ViewKey = "moji" | "ceka" | "otvoreni" | "zatvoreni" | "rok" | "dopuna" | "odobreno";
const VIEWS: Record<ViewKey, { label: string; params: Record<string, string | boolean>; filter?: (r: CaseRow) => boolean }> = {
  moji: { label: "Moji", params: { status: "otvoreni", assigned: "moji" } },
  ceka: { label: "Čeka dodjelu", params: { status: "podnesen" } },
  otvoreni: { label: "Svi otvoreni", params: { status: "otvoreni" } },
  zatvoreni: { label: "Zatvoreni", params: { status: "svi" }, filter: (r) => !OPEN_STATUSES.includes(r.status) },
  rok: { label: "Preko roka", params: { status: "otvoreni", overdue: true } },
  dopuna: { label: "Na dopuni", params: { status: "dopuna" } },
  odobreno: { label: "Odobreno 30 dana", params: { status: "odobren" },
    filter: (r) => !!r.decided_at && Date.now() - new Date(r.decided_at).getTime() < 30 * 86_400_000 },
};
const TABS: ViewKey[] = ["moji", "ceka", "otvoreni", "zatvoreni"];

/** Predmeti (Agencija): pregled, filtriranje i brza dodjela zahtjeva za registraciju/verifikaciju gazdinstava. */
export default function CasesPage() {
  const { t } = useT();
  const can = useCan();
  const nav = useNavigate();
  const qc = useQueryClient();
  const canAssign = can("cases:assign");
  const [params, setParams] = useSearchParams();
  const view = (params.get("v") as ViewKey) in VIEWS ? (params.get("v") as ViewKey) : (canAssign ? "ceka" : "moji");
  const setView = (v: ViewKey) => setParams((p) => { p.set("v", v); return p; }, { replace: true });
  const [q, setQ] = useState(params.get("q") ?? "");
  const [dq] = useDebouncedValue(q.trim(), 300);
  const [assign, setAssign] = useState<CaseRow | null>(null);

  const stats = useQuery({ queryKey: ["cases-stats"], queryFn: () => http.get<Stats>("/api/v1/cases/stats"), refetchInterval: 60_000 });
  const list = useQuery({
    queryKey: ["cases", view, dq],
    queryFn: () => http.get<CaseRow[]>("/api/v1/cases", { ...VIEWS[view].params, q: dq || undefined, limit: 500 }),
    placeholderData: (prev) => prev,
  });
  const rows = useMemo(() => {
    const f = VIEWS[view].filter;
    return (list.data ?? []).filter((r) => (f ? f(r) : true));
  }, [list.data, view]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["cases"] }); qc.invalidateQueries({ queryKey: ["cases-stats"] }); };

  const s = stats.data;
  const kpis: { key: ViewKey; label: string; value?: number; icon: ReactNode; color: string; hint: string }[] = [
    { key: "ceka", label: "Čeka dodjelu", value: s?.ceka_dodjelu, icon: <IconInbox size={20} />, color: "yellow", hint: "novi zahtjevi bez referenta" },
    { key: "moji", label: "Moji otvoreni", value: s?.moji, icon: <IconUser size={20} />, color: "blue", hint: "dodijeljeni meni" },
    { key: "rok", label: "Preko roka", value: s?.preko_roka, icon: <IconAlarm size={20} />, color: "red", hint: "otvoreni, rok istekao" },
    { key: "dopuna", label: "Na dopuni", value: s?.na_dopuni, icon: <IconArrowBackUp size={20} />, color: "orange", hint: "čeka se podnosilac" },
    { key: "odobreno", label: "Odobreno 30 dana", value: s?.odobreno_30d, icon: <IconCircleCheck size={20} />, color: "green", hint: "verifikovana gazdinstva" },
  ];
  const colorVar = (c: string) => `var(--mantine-color-${c}-6)`;

  return (
    <Stack gap="md">
      <CasesStyle />
      <PageHeader title="Predmeti"
        description="Zahtjevi za registraciju i verifikaciju gazdinstava: dodjela referentima, provjera podataka u Registru i katastru, dopuna i odluka."
        right={<Tooltip label={t("Osvježi")}><ActionIcon variant="light" size="lg" onClick={refresh} loading={list.isFetching}><IconRefresh size={18} /></ActionIcon></Tooltip>} />

      <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm">
        {kpis.map((k) => (
          <Paper key={k.key} withBorder radius="lg" p="md" className="case-kpi" data-active={view === k.key}
            style={{ borderLeftColor: colorVar(k.color) }} role="button" tabIndex={0} aria-pressed={view === k.key}
            onClick={() => setView(k.key)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setView(k.key)}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <div style={{ minWidth: 0 }}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700} truncate>{t(k.label)}</Text>
                {k.value === undefined ? <Skeleton h={30} w={40} mt={4} /> : (
                  <Text fz={30} fw={800} lh={1.15} c={k.key === "rok" && k.value > 0 ? "red.7" : undefined}>{k.value}</Text>
                )}
                <Text size="xs" c="dimmed" truncate>{t(k.hint)}</Text>
              </div>
              <ThemeIcon variant="light" color={k.color} size={38} radius="md">{k.icon}</ThemeIcon>
            </Group>
          </Paper>
        ))}
      </SimpleGrid>

      <Paper withBorder radius="lg">
        <Group justify="space-between" p="sm" gap="sm" wrap="wrap">
          <Group gap="sm" wrap="wrap">
            <SegmentedControl value={TABS.includes(view) ? view : ""} onChange={(v) => setView(v as ViewKey)} radius="md"
              data={TABS.map((k) => ({ value: k, label: t(VIEWS[k].label) }))} />
            {!TABS.includes(view) && (
              <Badge size="lg" variant="light" color="agri" rightSection={<CloseButton size="xs" onClick={() => setView("otvoreni")} aria-label={t("Ukloni filter")} />}>
                {t(VIEWS[view].label)}
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <TextInput leftSection={<IconSearch size={15} />} placeholder={t("Broj, BPG, podnosilac, e-pošta...")} value={q} w={280}
              onChange={(e) => setQ(e.currentTarget.value)} rightSection={q ? <CloseButton size="sm" onClick={() => setQ("")} /> : null} />
            <Text size="xs" c="dimmed">{rows.length} {t("predmeta")}</Text>
          </Group>
        </Group>
        <ScrollArea type="auto">
          <Table verticalSpacing={10} horizontalSpacing="md" fz="sm" miw={980} style={{ opacity: list.isFetching && list.isPlaceholderData ? 0.6 : 1 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("Broj")}</Table.Th><Table.Th>{t("Podnosilac")}</Table.Th><Table.Th>{t("Gazdinstvo / BPG")}</Table.Th>
                <Table.Th>{t("Opština")}</Table.Th><Table.Th miw={130}>{t("Status")}</Table.Th><Table.Th>{t("Referent")}</Table.Th>
                <Table.Th>{t("Rok")}</Table.Th><Table.Th ta="right" miw={50}>{t("KČ")}</Table.Th><Table.Th w={30} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.isLoading && Array.from({ length: 5 }).map((_, i) => (
                <Table.Tr key={i}><Table.Td colSpan={9}><Skeleton h={22} /></Table.Td></Table.Tr>
              ))}
              {rows.map((r) => {
                const open = OPEN_STATUSES.includes(r.status);
                return (
                  <Table.Tr key={r.id} className="case-row" tabIndex={0} onClick={() => nav(`/predmeti/${r.id}`)}
                    onKeyDown={(e) => e.key === "Enter" && nav(`/predmeti/${r.id}`)}
                    style={r.overdue ? { boxShadow: "inset 3px 0 0 var(--mantine-color-red-6)" } : undefined}>
                    <Table.Td>
                      <Text fw={750} size="sm" ff="monospace" style={{ whiteSpace: "nowrap" }}>{r.number}</Text>
                      <Text size="xs" c="dimmed">{r.submitted_at ? fmtDate(r.submitted_at) : "—"}</Text>
                    </Table.Td>
                    <Table.Td maw={220}>
                      <Text size="sm" fw={600} truncate>{r.applicant_name ?? "—"}</Text>
                      <Text size="xs" c="dimmed" truncate>{r.applicant_email}</Text>
                    </Table.Td>
                    <Table.Td maw={220}>
                      <Text size="sm" truncate>{r.holding_name ?? "—"}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">{r.bpg ? `BPG ${r.bpg}` : t("bez BPG")}</Text>
                    </Table.Td>
                    <Table.Td>
                      {r.opstina ? <Group gap={4} wrap="nowrap"><IconMapPin size={13} color="var(--mantine-color-dimmed)" /><Text size="sm" style={{ whiteSpace: "nowrap" }}>{r.opstina}</Text></Group> : "—"}
                    </Table.Td>
                    <Table.Td><CaseStatusBadge status={r.status} /></Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        {r.assigned_name ? <Text size="sm" truncate maw={160}>{r.assigned_name}</Text> : <Text size="sm" c="dimmed">—</Text>}
                        {canAssign && open && (
                          <Button size="compact-xs" variant={r.assigned_to ? "subtle" : "light"} color={r.assigned_to ? "gray" : "agri"}
                            leftSection={!r.assigned_to ? <IconUserPlus size={13} /> : undefined}
                            onClick={(e) => { e.stopPropagation(); setAssign(r); }}>
                            {t(r.assigned_to ? "Promijeni" : "Dodijeli")}
                          </Button>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: "nowrap" }}><DueText due={r.due_date} overdue={r.overdue} closed={!open} /></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={700}>{r.parcels}</Text></Table.Td>
                    <Table.Td><IconChevronRight size={16} color="var(--mantine-color-dimmed)" /></Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          {!list.isLoading && !rows.length && (
            <Stack align="center" gap={4} py="xl">
              <ThemeIcon size={48} radius="xl" variant="light" color="agri"><IconInbox size={26} /></ThemeIcon>
              <Empty text={dq ? "Nema predmeta za zadatu pretragu" : "Nema predmeta u ovom pregledu"} />
            </Stack>
          )}
          {list.error && <Text c="red" p="md">{(list.error as Error).message}</Text>}
        </ScrollArea>
      </Paper>

      <AssignModal opened={!!assign} caseId={assign?.id ?? null} caseNumber={assign?.number} current={assign?.assigned_to}
        onClose={() => setAssign(null)} onDone={refresh} />
    </Stack>
  );
}
